import type { WorkspacePulseEvent, WorkspacePulseSnapshot } from '../../aiAgentBridge'

export interface AiOperationalPulseHint {
  id: string
  summary: string
  severity?: string
  targetId?: string
}

const severityForPulse = (pulse: WorkspacePulseSnapshot) => {
  if (pulse.healthState === 'CRITICAL') return 'CRITICAL'
  if (pulse.healthState === 'WATCH') return 'WATCH'
  return 'INFO'
}

const uniqueHints = (hints: AiOperationalPulseHint[]) => {
  const seen = new Set<string>()
  return hints.filter((hint) => {
    const key = `${hint.id}:${hint.targetId ?? ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const workspacePulseToOperationalHints = (
  pulse: WorkspacePulseSnapshot | null | undefined,
  events: WorkspacePulseEvent[] = [],
): AiOperationalPulseHint[] => {
  if (!pulse) return []
  const hints: AiOperationalPulseHint[] = []
  if (pulse.healthState !== 'HEALTHY' || pulse.riskScore > 0) {
    hints.push({
      id: `pulse-health-${pulse.workspaceId}`,
      targetId: pulse.workspaceId,
      severity: severityForPulse(pulse),
      summary: `Workspace Pulse is ${pulse.healthState.toLowerCase()} with risk score ${pulse.riskScore}.`,
    })
  }
  if (pulse.failedAutomationCount > 0) {
    hints.push({
      id: `pulse-automation-${pulse.workspaceId}`,
      targetId: pulse.workspaceId,
      severity: pulse.failedAutomationCount >= 3 ? 'CRITICAL' : 'WATCH',
      summary: `${pulse.failedAutomationCount} automation failure${pulse.failedAutomationCount === 1 ? '' : 's'} detected.`,
    })
  }
  if (pulse.stockShortageCount > 0) {
    hints.push({
      id: `pulse-resource-${pulse.workspaceId}`,
      targetId: pulse.workspaceId,
      severity: 'WATCH',
      summary: `${pulse.stockShortageCount} resource pressure signal${pulse.stockShortageCount === 1 ? '' : 's'} detected.`,
    })
  }
  if (pulse.budget.overBudgetCount > 0 || pulse.budget.utilization >= 0.8) {
    hints.push({
      id: `pulse-budget-${pulse.workspaceId}`,
      targetId: pulse.workspaceId,
      severity: pulse.budget.utilization >= 1 ? 'CRITICAL' : 'WATCH',
      summary: `Budget utilization is ${Math.round(pulse.budget.utilization * 100)}% with ${pulse.budget.overBudgetCount} over-budget item${pulse.budget.overBudgetCount === 1 ? '' : 's'}.`,
    })
  }
  pulse.topRisks.slice(0, 6).forEach((risk) => {
    hints.push({
      id: `pulse-risk-${risk.id}`,
      targetId: risk.targetId || pulse.workspaceId,
      severity: severityForPulse(pulse),
      summary: `${risk.source}: ${risk.summary}`,
    })
  })
  events.slice(0, 6).forEach((event) => {
    hints.push({
      id: `pulse-event-${event.id}`,
      targetId: event.targetId || pulse.workspaceId,
      severity: event.severity,
      summary: `${event.title}: ${event.summary}`,
    })
  })
  return uniqueHints(hints)
}

export const mergeOperationalPulseHints = (
  ...groups: Array<AiOperationalPulseHint[] | null | undefined>
): AiOperationalPulseHint[] => uniqueHints(groups.flatMap((group) => group ?? [])).slice(0, 12)
